import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import {
  Button, Card, Input, Select, StatusPill, Table, EmptyState, Modal,
} from '../../../design-system';
import { money, day, dateInput, financialYear } from '../../../lib/tallyBooks';
import '../../styles/TallyPanel.css';

// The client's actual transactions — sales, purchases, receipts, journals.
//
// ⚠️ THE LARGEST THING THIS FEATURE STORES AND THE ONE NOBODY COULD SEE. A
// mid-size client runs to tens of thousands of vouchers a year; the sync pulled
// them, ingested them and advanced a cursor over them every night, and the
// application had no screen that showed a single one.
//
// ⚠️ CANCELLED AND OPTIONAL VOUCHERS ARE EXCLUDED BY DEFAULT, and the toggle
// names them rather than being a bare checkbox. An optional voucher is a draft
// the client has explicitly told Tally not to count — including it in anything
// a preparer reads overstates the books. The server applies the same default
// (`includeExcluded`), so the screen and the API agree.

const fy = financialYear();

function TallyEntries({ connectionId }) {
  const [vouchers, setVouchers] = useState([]);
  const [types, setTypes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  // Opens on the current financial year — the window the report endpoint
  // defaults to and the one the nightly incremental sync measures in, so the
  // first thing somebody sees is the period the rest of the module is about.
  const [from, setFrom] = useState(dateInput(fy.from));
  const [to, setTo] = useState(dateInput(fy.to));
  const [voucherType, setVoucherType] = useState('');
  const [includeExcluded, setIncludeExcluded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/api/tally/connections/${connectionId}/vouchers`, {
        params: {
          from: from || undefined,
          to: to || undefined,
          voucherType: voucherType || undefined,
          includeExcluded: includeExcluded ? '1' : undefined,
          limit: 500,
        },
      });
      setVouchers(res.data?.vouchers || []);
      setTypes(res.data?.voucherTypes || []);
      setTotal(res.data?.total || 0);
    } catch {
      // Keep what is on screen; a refresh blip is not "no entries".
    } finally {
      setLoading(false);
    }
  }, [connectionId, from, to, voucherType, includeExcluded]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card>
      <div className="tally-head">
        <div>
          <h3 className="tally-section-title">Entries</h3>
          <p className="tally-muted">
            The vouchers in this client&apos;s books over the chosen period, with
            the GST breakup as Tally computed it. Cancelled and optional vouchers
            are left out unless you ask for them.
          </p>
        </div>
      </div>

      <div className="tally-filters">
        <Input label="From" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        <Input label="To" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        <Select
          label="Voucher type"
          value={voucherType}
          onChange={(e) => setVoucherType(e.target.value)}
        >
          <option value="">All types</option>
          {types.map((t) => <option key={t} value={t}>{t}</option>)}
        </Select>
        <label className="tally-check">
          <input
            type="checkbox"
            checked={includeExcluded}
            onChange={(e) => setIncludeExcluded(e.target.checked)}
          />
          Include cancelled and optional
        </label>
      </div>

      {loading && !vouchers.length ? (
        <p className="tally-muted">Loading…</p>
      ) : !vouchers.length ? (
        <EmptyState
          icon="🧾"
          title="No entries in this period"
          description="Widen the dates, or check the sync status above — a backfill still in progress may not have reached this month yet."
        />
      ) : (
        <>
          <Table
            columns={[
              { key: 'date', header: 'Date', render: (v) => day(v.date) },
              {
                key: 'type',
                header: 'Type',
                render: (v) => (
                  <span className="tally-type-cell">
                    {v.voucherType || '—'}
                    {v.isCancelled && <StatusPill variant="danger" label="cancelled" />}
                    {v.isOptional && <StatusPill variant="warning" label="optional" />}
                  </span>
                ),
              },
              { key: 'no', header: 'No.', render: (v) => v.voucherNumber || '—' },
              { key: 'party', header: 'Party', render: (v) => v.partyLedgerName || '—' },
              {
                key: 'taxable',
                header: 'Taxable',
                render: (v) => <span className="tally-num">{money(v.taxableValue)}</span>,
              },
              {
                key: 'tax',
                header: 'GST',
                // Added up for DISPLAY only, from the four figures Tally itself
                // reported. Nothing is stored from this and nothing reconciles
                // against it — a total on a row is not a claim about a return.
                render: (v) => (
                  <span className="tally-num">
                    {money((v.cgst || 0) + (v.sgst || 0) + (v.igst || 0) + (v.cess || 0))}
                  </span>
                ),
              },
              {
                key: 'amount',
                header: 'Amount',
                render: (v) => <span className="tally-num">{money(v.amount)}</span>,
              },
              {
                key: 'open',
                header: '',
                render: (v) => (
                  <Button size="sm" variant="secondary" onClick={() => setOpen(v)}>
                    Lines
                  </Button>
                ),
              },
            ]}
            data={vouchers}
            keyField="_id"
          />
          {/* ⚠️ SAID OUT LOUD WHEN THE LIST IS CUT SHORT. A table that silently
              stops at 500 of 4,000 is a table somebody reads a total off. */}
          {total > vouchers.length && (
            <p className="tally-muted">
              {`Showing ${vouchers.length} of ${total} in this period. Narrow the dates or pick a voucher type.`}
            </p>
          )}
        </>
      )}

      <VoucherModal voucher={open} onClose={() => setOpen(null)} />
    </Card>
  );
}

/**
 * One voucher's double entry, and its stock lines where it has them.
 *
 * ⚠️ TALLY'S SIGN CONVENTION IS SHOWN AS TALLY REPORTED IT — not re-expressed
 * as our own debit/credit columns. The entries sum to zero in Tally's terms,
 * and converting them here would put a figure on this screen that disagrees
 * with the client's own display. See models/TallyVoucher.js.
 */
function VoucherModal({ voucher, onClose }) {
  if (!voucher) return null;
  const lines = voucher.ledgerEntries || [];
  const stock = voucher.inventoryEntries || [];

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${voucher.voucherType || 'Voucher'} ${voucher.voucherNumber || ''}`.trim()}
    >
      <p className="tally-muted">
        {day(voucher.date)}
        {voucher.partyLedgerName ? ` · ${voucher.partyLedgerName}` : ''}
        {voucher.placeOfSupply ? ` · ${voucher.placeOfSupply}` : ''}
      </p>
      {voucher.narration && <p className="tally-narration">{voucher.narration}</p>}

      {!lines.length ? (
        <p className="tally-muted">Tally reported no ledger lines for this voucher.</p>
      ) : (
        <Table
          columns={[
            { key: 'ledger', header: 'Ledger', render: (l) => l.ledgerName || '—' },
            { key: 'bill', header: 'Against', render: (l) => l.billName || '—' },
            {
              key: 'amount',
              header: 'Amount',
              render: (l) => <span className="tally-num">{money(l.amount)}</span>,
            },
          ]}
          data={lines.map((l, i) => ({ ...l, _k: `l${i}` }))}
          keyField="_k"
        />
      )}

      {stock.length > 0 && (
        <>
          <h4 className="tally-section-title">Stock</h4>
          <Table
            columns={[
              { key: 'item', header: 'Item', render: (s) => s.stockItemName || '—' },
              { key: 'hsn', header: 'HSN', render: (s) => s.hsn || '—' },
              { key: 'qty', header: 'Qty', render: (s) => `${s.quantity || 0} ${s.unit || ''}`.trim() },
              { key: 'rate', header: 'Rate', render: (s) => <span className="tally-num">{money(s.rate)}</span> },
              { key: 'amt', header: 'Amount', render: (s) => <span className="tally-num">{money(s.amount)}</span> },
            ]}
            data={stock.map((s, i) => ({ ...s, _k: `s${i}` }))}
            keyField="_k"
          />
        </>
      )}
    </Modal>
  );
}

export default TallyEntries;
